import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Tale til tekst med browserens egen talegenkendelse (beslutning 082).
 *
 * <p>
 * <b>Der bygges ikke noget selv, og lyden kommer aldrig forbi vores server.</b>
 * Android/Chrome og Safari har hver deres indbyggede genkendelse, og den er
 * bedre end noget, vi kunne stille op med - men <b>lyden sendes til Google
 * henholdsvis Apple</b> for at blive lavet om til tekst. Det skal stå i
 * brugerfladen, så ingen taler kundens navn og adresse ind i noget, de tror
 * bliver i huset.
 * </p>
 *
 * <p>
 * <b>Findes den ikke, er det ikke en fejl.</b> Telefonens eget tastatur har
 * selv en mikrofonknap, og feltet er et almindeligt tekstfelt. Derfor svarer
 * hooken blot <c>supported: false</c>, og knappen vises ikke - i stedet for en
 * knap, der ikke virker.
 * </p>
 */

/** Det lille udsnit af Web Speech API, vi bruger. Typerne findes ikke i TypeScripts DOM-lib. */
interface SpeechRecognitionLike {
  lang: string
  continuous: boolean
  interimResults: boolean
  start(): void
  stop(): void
  abort(): void
  onresult: ((event: SpeechRecognitionEventLike) => void) | null
  onerror: ((event: { error: string }) => void) | null
  onend: (() => void) | null
}

interface SpeechRecognitionEventLike {
  results: ArrayLike<ArrayLike<{ transcript: string }>>
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike

function findConstructor(): SpeechRecognitionConstructor | null {
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionConstructor
    webkitSpeechRecognition?: SpeechRecognitionConstructor
  }

  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

/** Fejlteksterne skrives, så de siger hvad man gør - ikke hvad koden hedder. */
function message(error: string) {
  switch (error) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Telefonen gav ikke adgang til mikrofonen. Skriv noten i stedet, eller giv adgang under indstillinger.'
    case 'no-speech':
      return 'Der kom ingen lyd. Prøv igen, eller skriv noten.'
    case 'network':
      return 'Talegenkendelsen kræver forbindelse. Skriv noten i stedet.'
    case 'aborted':
      return null
    default:
      return 'Talegenkendelsen gik i stå. Skriv noten i stedet.'
  }
}

export interface Dictation {
  /** Har browseren overhovedet talegenkendelse? Er den false, vises knappen ikke. */
  supported: boolean
  listening: boolean
  error: string | null
  /**
   * Tænder mikrofonen. `existing` er det, der allerede står i feltet - det sagte
   * lægges OVEN I det, så en rettelse med tastaturet ikke slettes af næste ord.
   */
  start: (existing: string) => void
  stop: () => void
}

/**
 * Lytter og kalder <c>onText</c> med hele feltets nye indhold, hver gang der er
 * kommet mere.
 */
export function useDictation(onText: (text: string) => void): Dictation {
  const [supported] = useState(() => findConstructor() !== null)
  const [listening, setListening] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const recognition = useRef<SpeechRecognitionLike | null>(null)
  const base = useRef('')
  const latest = useRef(onText)

  useEffect(() => {
    latest.current = onText
  }, [onText])

  const stop = useCallback(() => {
    recognition.current?.stop()
    setListening(false)
  }, [])

  const start = useCallback((existing: string) => {
    const Ctor = findConstructor()
    if (!Ctor) return

    setError(null)
    base.current = existing

    const instance = new Ctor()
    instance.lang = 'da-DK'
    // continuous: manden fortaeller en hel saetning med pauser i, mens han kigger
    // paa tavlen. Stopper den ved foerste pause, bliver halvdelen vaek.
    instance.continuous = true
    instance.interimResults = true

    instance.onresult = (event) => {
      let spoken = ''
      for (let i = 0; i < event.results.length; i++) {
        spoken += event.results[i][0].transcript
      }

      const before = base.current.trimEnd()
      latest.current(before ? `${before} ${spoken.trimStart()}` : spoken)
    }

    instance.onerror = (event) => {
      const text = message(event.error)
      if (text) setError(text)
      setListening(false)
    }

    instance.onend = () => setListening(false)

    recognition.current = instance
    setListening(true)

    try {
      instance.start()
    } catch {
      // start() kaster, hvis der allerede lyttes. Så er alt, som det skal være.
    }
  }, [])

  // Forlader man siden, mens mikrofonen lytter, skal den slukkes - ellers bliver
  // den lille roede prik i statuslinjen staaende.
  useEffect(() => () => recognition.current?.abort(), [])

  return { supported, listening, error, start, stop }
}
